import { Card, Title } from '@yoast/ui-library';
import { type ChartData, type ChartOptions } from 'chart.js';
import { useEffect, useState } from 'react';
import { Radar } from 'react-chartjs-2';
import DataQuadrant, { DataQuadrantCell } from '../../components/DataQuadrant';
import TextScore from '../../components/TextScore';
import TooltipUncontrolled from '../../components/TooltipUncontrolled';
import VisibilityDelta from '../../components/VisibilityDelta';
import useScan from '../../hooks/useScan';
import { TOOLTIP_MOBILE_MAX_WIDTH } from '../../utils/breakpoints';
import {
  baseChartOptions,
  createRadarChartGradient,
} from '../../utils/chartSetup';

export default function VisibilityScore() {
  const { data: scan, isLoading } = useScan();

  // Extract scores from scan
  const scoresStandardized = scan?.scores?.standardized;

  // Extract metrics
  const mentions = scan?.mentions?.mentionedInNumberOfQueries?.value ?? 0;
  const mentionsPrev =
    scan?.mentions?.mentionedInNumberOfQueries?.previous?.difference;

  const citationsScore = scan?.scores?.standardized?.citations?.value ?? 0;
  const citations = scan?.citations?.ownBrandTotal?.value ?? 0;
  const citationsDelta = scan?.citations?.ownBrandTotal?.previous?.difference;

  // Find self ranking from rankings list
  const selfRankingObj = scan?.rankings?.list?.find(
    (item) => item.brand?.isSelf === true,
  );
  const ranking = selfRankingObj?.rank?.value ?? '—';

  let rankingPrev = selfRankingObj?.rank?.previous?.difference;
  if (rankingPrev) {
    // A climb in the rankings is shown as a negative difference. To ensure proper visual delta's, invert the negative/positive sign.
    rankingPrev = rankingPrev * -1;
  }

  // Standardized percentages
  const visibilityPct = scoresStandardized?.visibility?.value ?? 0;
  const prevVisibilityPct = scoresStandardized?.visibility?.previous?.value;

  const rankingPct = scoresStandardized?.ranking?.value ?? 0;
  const sentimentPct = scoresStandardized?.sentiment?.value ?? 0;
  const mentionsPct = scoresStandardized?.mentions?.value ?? 0;

  // logs all

  const chartData: ChartData<'radar'> = {
    labels: ['Mentions', 'Ranking', 'Citations', 'Sentiment'],
    datasets: [
      {
        data: [mentionsPct, rankingPct, citationsScore, sentimentPct],
        backgroundColor: createRadarChartGradient,
        pointBackgroundColor: 'rgba(166, 30, 105, 1)',
      },
    ],
  };

  const radarOptions: ChartOptions<'radar'> = {
    ...baseChartOptions,
    plugins: {
      ...baseChartOptions.plugins,
      tooltip: {
        mode: 'point' as const,
        intersect: true,
        callbacks: {
          label: (context) => {
            const label = context.label || '';
            const value = context.parsed.r || 0;
            return `${label}: ${value}%`;
          },
        },
      },
    },
    interaction: {
      mode: 'point' as const,
      intersect: true,
    },
    scales: {
      r: {
        beginAtZero: true, // set baseline to 0 in the center
        min: 0,
        max: 100,
        ticks: {
          stepSize: 20, // show guide lines in increments of 20
        },
      },
    },
  };

  const [tooltipPosition, setTooltipPosition] = useState<'left' | 'top'>(
    typeof window !== 'undefined' && window.innerWidth < TOOLTIP_MOBILE_MAX_WIDTH
      ? 'top'
      : 'left',
  );
  useEffect(() => {
    function handleResize() {
      setTooltipPosition(
        window.innerWidth < TOOLTIP_MOBILE_MAX_WIDTH ? 'top' : 'left',
      );
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <Card className="yst-overflow-visible md:yst-col-span-2">
      <Card.Content>
        <Title size="3" className="yst-mb-2">
          AI Visibility Index (AIVI)
        </Title>

        <TextScore score={visibilityPct} total={100} isLoading={isLoading} />
        <VisibilityDelta
          current={visibilityPct}
          previous={prevVisibilityPct}
          className="yst-mb-4"
        />

        {/* mobile: stacked, md: grid */}
        <div className="yst-flex yst-flex-col yst-items-center yst-justify-around yst-justify-items-center yst-gap-8 md:yst-flex-row">
          {/* https://www.chartjs.org/docs/latest/charts/radar.html */}

          {/* 50% col */}
          <div className="yst-radar-chart-container yst-relative yst-aspect-square yst-h-full yst-w-full md:yst-flex md:yst-w-1/2 md:yst-items-center md:yst-justify-center">
            <Radar data={chartData} options={radarOptions} />

            <TooltipUncontrolled
              id="tooltip-radar-chart"
              className="yst-absolute yst-right-2 yst-top-2"
              position="left"
            >
              {/* https://www.figma.com/design/zH43jZJuvZyPaqrod8lG5u?node-id=138-2053#1353629918 */}
              The AI Visibility Index (AIVI) shows how visible your brand is
              across major AI platforms like ChatGPT and Perplexity, based on mentions,
              citations, sentiment, and rankings.
            </TooltipUncontrolled>
          </div>

          {/* 50% col */}
          <div className="yst-mt-6 yst-w-full md:yst-mt-0 md:yst-flex md:yst-w-1/2 md:yst-items-center">
            <DataQuadrant>
              <DataQuadrantCell
                value={mentions}
                label="Mentions"
                // https://www.figma.com/design/zH43jZJuvZyPaqrod8lG5u?node-id=138-2053#1353632379
                tooltip="Mentions show how often your brand appears in AI-generated answers to the questions we send to LLMs."
                borderBottom
                borderRight
                delta={mentionsPrev}
                tooltipPosition={tooltipPosition}
              />
              <DataQuadrantCell
                value={
                  typeof ranking === 'number' &&
                  Number.isFinite(ranking) &&
                  ranking > 0 ? (
                    <>
                      #{ranking}
                      {ranking === 1 && ' 🏆'}
                    </>
                  ) : (
                    '—'
                  )
                }
                label="Competitor ranking"
                // https://www.figma.com/design/zH43jZJuvZyPaqrod8lG5u?node-id=138-2053#1353632973
                tooltip="Ranking shows the position your brand is mentioned in AI-generated answers&mdash;whether in a numbered list or the order of appearance in full-text responses."
                borderBottom
                delta={rankingPrev}
              />
              <DataQuadrantCell
                value={citations}
                label="Citations"
                // https://www.figma.com/design/zH43jZJuvZyPaqrod8lG5u?node-id=138-2053#1353632769
                tooltip="Citations are the source links found in AI-generated answers. They form the basis for mentions, sentiment, and ranking insights."
                borderRight
                delta={citationsDelta}
                tooltipPosition={tooltipPosition}
              />
              <DataQuadrantCell
                value={sentimentPct ? `${sentimentPct}%` : '—'}
                label="Positive sentiment"
                // https://www.figma.com/design/zH43jZJuvZyPaqrod8lG5u?node-id=138-2053#1353633172
                tooltip="Sentiment reflects how positively, neutrally, or negatively your brand is mentioned in AI-generated answers, based on the cited sources."
                delta={
                  scan?.scores?.standardized?.sentiment?.previous?.difference
                }
              />
            </DataQuadrant>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
}
